import { ITachographDaySegment } from '@src/app/interfaces/tachograph.interface';
import { DayBounds, DrawSegment } from './tachograph-chart.types';
import { intervalToDuration } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';

export const DAY_MS = 24 * 60 * 60 * 1000;

export const toDayBounds = (day: Date): DayBounds => {
  const d = new Date(day.getFullYear(), day.getMonth(), day.getDate());
  const start = d.getTime();
  return { start, end: start + DAY_MS };
};

export const clamp = (v: number, min: number, max: number) =>
  Math.min(max, Math.max(min, v));

export function clampSegments(
  segments: ITachographDaySegment[],
  bounds: DayBounds
): DrawSegment[] {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const localBounds: DayBounds = {
    start: utcToZonedTime(bounds.start, timeZone).getTime(),
    end:   utcToZonedTime(bounds.end, timeZone).getTime(),
  };

  return segments
    .map(s => {
      const rawFrom = new Date(s.From).getTime();
      const rawTo   = new Date(s.To).getTime();

      const fromLocal = utcToZonedTime(rawFrom, timeZone).getTime();
      const toLocal   = utcToZonedTime(rawTo,   timeZone).getTime();

      const from = clamp(fromLocal, localBounds.start, localBounds.end);
      const to   = clamp(toLocal,   localBounds.start, localBounds.end);

      const duration = Math.max(0, to - from);
      if (duration <= 0) return null;

      return { from, to, rawFrom, rawTo, state: s.State, duration };
    })
    .filter((x): x is DrawSegment => !!x);
}

export const pad2 = (n: number) => String(n).padStart(2, '0');

export const formatTimeHM = (t: number) => {
  const d = new Date(t);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

export const formatDurationShort = (from: number | Date, to: number | Date): string => {
  const dur = intervalToDuration({ start: from, end: to });

  const parts: string[] = [];
  if (dur.hours) parts.push(`${dur.hours}h`);
  if (dur.minutes) parts.push(`${dur.minutes}m`);

  if (parts.length === 0) parts.push('0m');

  return parts.join(' ');
};
